import MobileSpaceFiltersBase from 'components/mobileSpaceFilters';
import Pager from 'components/pager';
import SearchMap from 'components/searchMap';
// components
import SearchResultFilters, {
  SortOption,
} from 'components/searchResultFilters';
import LoadingOverlay from 'components/shared/loadingOverlay';
import SpaceFilters from 'components/spaceFilters';
import SpaceListBase from 'components/spaceList';
import Venue from 'components/venue';
import { TABLET_WIDTH } from 'constants/breakpoints';
import { RESULTS_PER_PAGE } from 'constants/fieldData';
import { RADIX } from 'constants/radix';
import { sortFields } from 'constants/sortFields';
import useClientServices from 'hooks/useClientServices';
import useIsMobile from 'hooks/useIsMobile';

import useSpaceSearch, {
  emptyData,
  SpaceSearchData,
} from 'hooks/useSpaceSearch';
import useStoredBooking from 'hooks/useStoredBooking';
import Head from 'next/head';
import { useRouter } from 'next/router';
import {
  ComponentProps,
  FC,
  type MouseEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
// context
import { SearchContext } from 'state/search';
import type { Venue as VenueModel } from 'types/domain';
// constants
import { spaceDisabledForService } from 'utils/booking';
import { useTranslation } from 'utils/i18n';
import { getVenueImage } from 'utils/venueImages';

import useHeader from './hooks/useHeader';

interface Props {
  selectedVenueId?: number;
  selectedSpaceIds: number[];
  selectSpace: (space: ListSpace, venue: ListVenue) => void;
  initialData?: SpaceSearchData;
}

// The search API enriches each venue with fields (organisation, discounts)
// that aren't part of the canonical `Venue` type. Model them locally so the
// existing usage type-checks.
type ResultVenue = VenueModel & {
  organisation?: { name: string };
  discounts?: ComponentProps<typeof Venue>['discounts'];
};

const MobileSpaceFilters = MobileSpaceFiltersBase as FC<
  ComponentProps<typeof MobileSpaceFiltersBase> & { onChange?: unknown }
>;

// The shapes SpaceList's own onAddToBooking declares.
type ListSpace = Parameters<
  NonNullable<ComponentProps<typeof SpaceListBase>['onAddToBooking']>
>[0];
type ListVenue = Parameters<
  NonNullable<ComponentProps<typeof SpaceListBase>['onAddToBooking']>
>[1];

// The search response's rows are structurally compatible with what SpaceList
// renders but not declared identically; relax only these props.
const SpaceList = SpaceListBase as unknown as FC<
  Omit<
    ComponentProps<typeof SpaceListBase>,
    'spaces' | 'venue' | 'selectedSpaceIds'
  > & {
    spaces?: ListSpace[];
    venue?: ListVenue;
    selectedSpaceIds?: number[];
    discounts?: ComponentProps<typeof Venue>['discounts'];
  }
>;

const SearchResults: FC<Props> = ({
  selectedVenueId,
  selectedSpaceIds = [],
  selectSpace = () => {},
  initialData = emptyData,
}) => {
  const { t } = useTranslation('common');
  const router = useRouter();

  const { filters, actions: searchActions } = useContext(SearchContext);
  const location = filters.latLng
    ? t('shared.current_location')
    : filters.location;

  const [{ service: serviceId = '' } = {}] = useStoredBooking();
  const { data: services } = useClientServices();

  const header = useHeader(filters);

  const service = useMemo(
    () =>
      serviceId
        ? services.find(({ id }) => id === Number(serviceId))
        : undefined,
    [services, serviceId]
  );
  const [currentView, setCurrentView] = useState<'list' | 'map'>('list');
  const [validSortOptions, updateValidSortOptions] = useState<SortOption[]>(
    Object.keys(sortFields) as SortOption[]
  );
  const [currentPage, updateCurrentPage] = useState(1);
  const [mapVenue, setMapSelectedProperty] = useState<
    ResultVenue | undefined
  >();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_displayStickyNav, _toggleStickyPanel] = useState(false);
  // Measured after mount so the first client render matches the server's.
  // Mobile-first, which is what the server renders: it has no viewport.
  const isMobileView = useIsMobile(TABLET_WIDTH, true);

  const {
    data: {
      venues: results = [],
      alternativeVenues = [],
      totalVenues,
      totalSpaces,
      aggregates,
    } = initialData,
    loading: loadingResults,
  } = useSpaceSearch({ filters, viewType: currentView, page: currentPage });
  const { actions } = useContext(SearchContext);

  useEffect(() => {
    actions.updatePreviousSearches(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions.updatePreviousSearches, filters]);

  useEffect(() => {
    updateCurrentPage(1);
  }, []);

  const searchResults = results.length > 0 ? results : alternativeVenues;

  useEffect(() => {
    const resetSearch = (url: string) => {
      if (url.startsWith('/properties/') || url.startsWith('/rooms/')) {
        return;
      }

      if (url !== '/sign-in' && router.pathname !== '/sign-in')
        searchActions.resetSearch();
    };

    router.events.on('routeChangeStart', resetSearch);
    return () => router.events.off('routeChangeStart', resetSearch);
  }, [router, searchActions]);

  useEffect(() => {
    if (loadingResults && currentView === 'list') {
      setMapSelectedProperty(undefined);
    }
  }, [loadingResults, currentView]);

  if (location === '' && validSortOptions.includes('Distance')) {
    const availableSortOptions = validSortOptions.filter(
      field => field !== 'Distance'
    );

    updateValidSortOptions(availableSortOptions);
  }

  const handlePageChange = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    const nextPage = parseInt(String(currentPage), RADIX) + 1;

    updateCurrentPage(nextPage);
  };

  const handleChangeSortBy = (sortBy: string) => {
    updateCurrentPage(1);
    searchActions.onSetSort(sortBy);
  };

  const handleChangeView = (switchOn?: boolean) => {
    updateCurrentPage(1);
    setCurrentView(switchOn ? 'list' : 'map');
  };

  const handleSelectVenueInMap = (selectedVenue?: ResultVenue) => {
    if (currentView === 'list') {
      handleChangeView(false);
    }

    setMapSelectedProperty(selectedVenue);
  };

  const noResultsMessage = () => {
    if (!location) {
      return t('search_results.alternative_venues.no_matches');
    }

    if (location && alternativeVenues.length > 0) {
      return (
        <>
          {t('search_results.alternative_venues.no_location_results')}
          <strong>{location}</strong>.{' '}
          {t('search_results.alternative_venues.nearest_properties')}
        </>
      );
    }

    return (
      <>
        {t('search_results.alternative_venues.no_location_results')}
        <strong>{location}</strong>.{' '}
        {t('search_results.alternative_venues.check_spelling')}
      </>
    );
  };

  return (
    <>
      <Head>
        <title>{header.title}</title>
        <meta
          name="description"
          content={header.description}
          key="description"
        />
        <meta name="title" content={header.title} key="title" />
      </Head>
      <div className="venue-search" id="venue-search">
        {isMobileView && results.length > 0 && (
          <MobileSpaceFilters
            changeViewType={handleChangeView}
            currentViewType={currentView}
            updateCurrentPage={updateCurrentPage}
            aggregates={aggregates}
          />
        )}
        <SearchResultFilters
          location={location}
          roomsTotal={results.length > 0 ? totalSpaces : 0}
          propertiesTotal={results.length > 0 ? totalVenues : 0}
          currentSortBy={filters.sort}
          currentViewType={currentView}
          changeViewType={handleChangeView}
          changeSortBy={handleChangeSortBy}
          sortOptions={validSortOptions}
          loading={loadingResults}
        />
        {!loadingResults && currentView === 'list' && results.length < 1 && (
          <div className="venue-search__alternative_venues_message">
            <p>{noResultsMessage()}</p>
          </div>
        )}
        {!isMobileView && (
          <SpaceFilters
            currentViewType={
              currentView as ComponentProps<
                typeof SpaceFilters
              >['currentViewType']
            }
            updateCurrentPage={updateCurrentPage}
            aggregates={aggregates}
          />
        )}

        <LoadingOverlay active={loadingResults && currentView === 'list'}>
          {currentView === 'list' && (
            <>
              {searchResults.length > 0 && (
                <ul className="venue-search__list">
                  {(searchResults as ResultVenue[]).map(venue => (
                    <Venue
                      key={`${venue.id}-${(venue.spaces || [])
                        .map(({ id }) => id)
                        .join('-')}`}
                      id={venue.id}
                      name={venue.name}
                      url={`${t('navigation.venues.url')}/${venue.id}`}
                      image={getVenueImage(venue)}
                      latLng={venue.latLng}
                      location={location}
                      distance={venue.distance}
                      showSpacesDefault
                      onClickMap={() => handleSelectVenueInMap(venue)}
                      facets={venue.facets}
                      floorplans={venue.floorplans}
                      discount={venue.discount}
                      discounts={venue.discounts}
                      venueSpaces={venue.spaces}
                      organisationName={venue.organisation!.name}
                      spaces={() => (
                        <SpaceList
                          key={(venue.spaces || [])
                            .map(({ id }) => id)
                            .join('-')}
                          venue={venue}
                          spaces={venue.spaces}
                          selectedVenueId={selectedVenueId}
                          selectedSpaceIds={selectedSpaceIds}
                          onAddToBooking={selectSpace}
                          spaceServiceClash={spaceDisabledForService(service)}
                          discounts={venue.discounts}
                          fetchSpaces={false}
                        />
                      )}
                    />
                  ))}
                </ul>
              )}

              {results.length > 0 && (
                <Pager
                  className="venue-search--pager"
                  loading={loadingResults}
                  currentPage={currentPage}
                  perPage={RESULTS_PER_PAGE}
                  total={totalVenues}
                  onClick={handlePageChange}
                />
              )}
            </>
          )}
          {currentView === 'map' && (
            <div className="search-map">
              <SearchMap
                location={location}
                venues={results.length ? results : alternativeVenues}
                loading={loadingResults}
                selectedVenue={mapVenue}
                onSelectVenue={handleSelectVenueInMap}
                geolocation={filters.latLng ?? undefined}
              />
              {mapVenue && (
                <SpaceList
                  spaces={mapVenue.spaces || []}
                  venue={mapVenue}
                  selectedVenueId={selectedVenueId}
                  selectedSpaceIds={selectedSpaceIds}
                  onAddToBooking={selectSpace}
                  spaceServiceClash={spaceDisabledForService(service)}
                />
              )}
              {!mapVenue && (
                <p className="search-map__text">
                  {t('location_map.select_venue_prompt')}
                </p>
              )}
            </div>
          )}
        </LoadingOverlay>
      </div>
    </>
  );
};

export default SearchResults;
